import React, {useState} from 'react';
import PropTypes from 'prop-types';

import {useNavigate} from "react-router-dom";
import Typography from "@mui/material/Typography";
import {Box, Flex} from "reflexbox";
import SkeletonImage from "../common/SkeletonImage";
import {AccessTime, Directions, DirectionsWalk, FlagOutlined, KeyboardArrowLeft} from "@mui/icons-material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from '@mui/material/styles';
import {ToEnd, ToMap} from "../helpers/routes";
import {ANALAGOUSCOLOR1, ANALAGOUSCOLOR2, SECONDARYCOLOR} from "../helpers/ThemeSettings";
import {useSelector} from "react-redux";
import {courseFriendlyName, courseHasMap} from "../courseContent/courseObjects";
import SwipeableImage from "../common/SwipeableImage";
import BottomBar from "../common/BottomBar";

const CourseDetails = (props) => {
    const theme = useTheme();
    const { distance, time, destinationQuant, locationInCity, city, name, startOptions } = props.course;
    const ctaLabel = props.ctaLabel ?? 'Select';
    const viewOnly = props.viewOnly ?? false;
    const courseProgress = useSelector((state) => state.team?.teamDetails?.courseProgress);
    let navigate = useNavigate();

    const [selectedCourse, setSelectedCourse] = useState(startOptions[1] ? null : startOptions[0].courseName);
    const [error, setError] = useState(false);
    const [showLongDescription, setShowLongDescription] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const hasCourseMap = startOptions.some((option) => courseHasMap(option.courseName));

    //logic to create buttons that lets you resume course or see score summary
    let resumeAndScoreSummaryButtons = [];
    let prevStarted = false;
    const generateResAndSumButton = (courseName) => {
        if (courseProgress?.[courseName]) {
            prevStarted = true;
            courseProgress[courseName] === ToEnd(courseName) ?
                resumeAndScoreSummaryButtons.push(<Button key={courseName} variant={'contained'} color={'primary'}
                                                          onClick={() => props.handleCourseSeeSummary(courseName)}
                                                          style={{marginTop: '16px', marginBottom: '8px'}}> See Score
                    for {courseFriendlyName(courseName)} </Button>)
                : resumeAndScoreSummaryButtons.push(<Button key={courseName} variant={'contained'} color={'primary'}
                                                            onClick={() => props.handleCourseResume(courseName, courseProgress[courseName])}
                                                            style={{marginTop: '16px'}}>Resume {courseFriendlyName(courseName)} </Button>)
        }
    };
    startOptions[0] && generateResAndSumButton(startOptions[0].courseName);
    startOptions[1] && generateResAndSumButton(startOptions[1].courseName);


    const handleStart = () => {
        if (!selectedCourse) {
            setError(true)
        } else if (!!selectedCourse) {
            props.handleCourseSelection(selectedCourse);
        }
    };

    const handleClick = (courseName) => {
        setError(false);
        setSelectedCourse(selectedCourse === courseName ? null : courseName)
    };

    let getDirectionsLink = startOptions[0]?.googleMapsLink;
    if (startOptions[1] && selectedCourse === startOptions[1]?.courseName) {
        getDirectionsLink = startOptions[1]?.googleMapsLink;
    }

    const startLocationOptionCard = (startOption, suggested) => (
        <Flex style={{
            flexDirection: 'column',
            width: '100%',
            display: 'flex',
            boxSizing: 'border-box',
            minHeight: '164px'
        }}>
            {<Typography variant={'caption'} style={{
                fontStyle: 'italic',
                color: ANALAGOUSCOLOR1
            }}> {suggested ? 'Suggested' : '\u00A0'} </Typography>}
            <Card onClick={() => handleClick(startOption.courseName)}
                  style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                      minHeight: '164px',
                      backgroundColor: selectedCourse === startOption.courseName ? alpha(ANALAGOUSCOLOR2, 0.08) : undefined,
                      border: selectedCourse === startOption.courseName ? `5px solid ${ANALAGOUSCOLOR2}` : `1px solid ${theme.palette.divider}`
                  }}>
                {!selectedCourse && <div id={'skeleton'} style={{height: 0}}>
                    <div style={{
                        width: '100%',
                        height: '175px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        animation: 'MuiSkeleton-keyframes-pulse 3s ease-in-out 0.5s infinite'
                    }}/>
                </div>}
                <Box style={{padding: '8px'}}>
                    <SkeletonImage src={startOption.icon} alt={startOption.name}/>
                </Box>
                <Typography> {startOption.name} </Typography>
            </Card>
        </Flex>);

    const DirectionsButton = (
        <a href={getDirectionsLink} target="_blank" rel="noreferrer" style={{textDecoration: 'none', width: '100%'}}>
            <Button color={'primary'} variant={'outlined'} disabled={!selectedCourse}
                    style={{margin: '8px 0', width: '100%'}}>
                <Directions fontSize="small" style={{marginRight: '4px'}}/> Directions to start
            </Button>
        </a>);

    return (<>
        <Flex onClick={() => navigate(-1)} style={{margin: '8px 0'}}>
            <KeyboardArrowLeft/> <Typography>Back</Typography>
        </Flex>
        <Card>
            <SwipeableImage
                images={props.course.images}
                showNav
                showNavArrows={false}
                overlayNavOnImage={false}
            />
            <Flex style={{flexDirection: 'column', padding: '16px'}}>
                <Typography variant={'h6'} style={{fontWeight: 'bold'}}> {name} </Typography>
                <Typography variant={'caption'} style={{paddingBottom: '16px'}}>
                    {locationInCity} · {city}
                </Typography>

                <Flex id={'quick stats'} style={{marginTop: '10px'}}>
                    <Flex flexDirection={'column'} style={{marginRight: '16px'}}>
                        <Flex>
                            <AccessTime style={{fontSize: 26, marginRight: 4}}/>
                            <Typography style={{fontWeight: '600'}}> {time}</Typography>
                        </Flex>
                        <Typography variant={'caption'} style={{marginLeft: '3px'}}>Time to finish</Typography>
                    </Flex>

                    <Flex flexDirection={'column'} style={{marginRight: '16px'}}>
                        <Flex>
                            <DirectionsWalk style={{fontSize: 26, marginRight: 4}}/>
                            <Typography style={{fontWeight: '600'}}> {distance}</Typography>
                        </Flex>
                        <Typography variant={'caption'} style={{marginLeft: '3px'}}>Walking Distance</Typography>
                    </Flex>

                    {destinationQuant && <Flex flexDirection={'column'} style={{marginRight: '16px'}}>
                        <Flex>
                            <FlagOutlined style={{fontSize: 26, marginRight: 4}}/>
                            <Typography style={{fontWeight: '600'}}> {destinationQuant}</Typography>
                        </Flex>
                        <Typography variant={'caption'}
                                    style={{marginLeft: '3px'}}>Memorial{destinationQuant > 1 && "s"}</Typography>
                    </Flex>}

                </Flex>

                <Divider sx={{ my: 3 }} />
                {showLongDescription ?
                    <Typography variant={'body1'}> {props.course?.longDescription} <Typography color={'primary'}
                                                                                               style={{
                                                                                                   display: 'inline',
                                                                                                   fontWeight: '600'
                                                                                               }}
                                                                                               onClick={() => setShowLongDescription(false)}> Show
                        less </Typography> </Typography>
                    : <Typography variant={'body1'}> {props.course?.shortDescription} <Typography color={'primary'}
                                                                                                  style={{
                                                                                                      display: 'inline',
                                                                                                      fontWeight: '600'
                                                                                                  }}
                                                                                                  onClick={() => setShowLongDescription(true)}> Show
                        more </Typography> </Typography>
                }

                <Typography variant={'h6'} style={{marginTop: '24px'}}>{startOptions[1] ? "Starting Options" : "Starting location"}</Typography>
                <Flex flexDirection={'column'} style={{marginBottom: '16px'}}>
                    {startOptions.map((option, index) => (
                        <Flex key={index} id={index} alignItems={'center'} style={{padding: "8px 0"}}>
                            <img src={option.icon} alt={option.name} style={{width: '60px', marginRight: '8px'}}/>
                            <Flex flexDirection={'column'}>
                                <Typography>{option.name}</Typography>
                                {index===0 && startOptions[1] && <Typography variant={"caption"}>Suggested</Typography>}
                            </Flex>
                        </Flex>
                    ))}
                </Flex>




                {resumeAndScoreSummaryButtons}
                {startOptions[1] ? null : DirectionsButton}
                {hasCourseMap && (
                    <Button variant={'outlined'} color={'primary'}
                            onClick={() => navigate(ToMap(startOptions[0]?.courseName))}> View Course
                        Map</Button>
                )}
            </Flex>
        </Card>
        {/*<div id={'bottom spacer'} style={{height: '16px', width: '100%'}}/>*/}
        <BottomBar>
            {viewOnly ? (
                <Button variant="contained" color="primary" style={{width: '100%'}}
                        onClick={() => props.handleCourseSelection()}>
                    {ctaLabel}
                </Button>
            ) : (
                <Button variant={prevStarted ? 'outlined' : 'contained'} color="primary" style={{width: '100%'}}
                        onClick={startOptions[1] ? () => setOpenDialog(true) : handleStart}>
                    {startOptions[1] ? "Choose Starting Point Option" : ctaLabel}
                </Button>
            )}
        </BottomBar>

        <Dialog open={openDialog} onClose={() => {
            setOpenDialog(false);
            setError(false)
        }}>
            <Flex style={{padding: '16px 24px', flexDirection: 'column', alignItems: 'center', textAlign: 'center'}}>
                <Typography variant={'h6'} color={'primary'} style={{fontWeight: 'bold'}}> Choose Starting
                    Point </Typography>
                <Typography variant={'caption'}> You end where you don't start</Typography>
                <Flex style={{textAlign: 'center', margin: '20px 0', minHeight: '175px'}}>
                    {startLocationOptionCard(startOptions[0], true)}
                    <div style={{width: '24px'}}/>
                    {startOptions[1] && startLocationOptionCard(startOptions[1])}
                </Flex>
                <Button color={selectedCourse ? 'primary' : 'inherit'} variant={'contained'}
                        style={{margin: '8px 0', width: '100%', maxWidth: '360px'}}
                        onClick={handleStart}>{ctaLabel}</Button>
                <Typography variant={'caption'} style={{display: !error && 'none', color: SECONDARYCOLOR}}> Select an
                    option to start </Typography>
                {DirectionsButton}
            </Flex>
        </Dialog>
    </>)
};

CourseDetails.propTypes = {
    course: PropTypes.shape({
        name: PropTypes.string.isRequired,
        images: PropTypes.array.isRequired,
        shortDescription: PropTypes.string.isRequired,
        longDescription: PropTypes.string.isRequired,
        distance: PropTypes.string.isRequired,
        time: PropTypes.string.isRequired,
        destinationQuant: PropTypes.number,
        handleCourseSelection: PropTypes.func.isRequired,
        handleCourseSeeSummary: PropTypes.func.isRequired,
        handleCourseResume: PropTypes.func.isRequired,
    }),
    ctaLabel: PropTypes.string,
};

export default CourseDetails;